import type { Incident, IncidentGroup } from '../../services/incidentsApi';
import { resolveIncidentGroupActionTarget } from '../../utils/incidentGroupKey';
import {
  isGroupedIncident,
} from '../../domain/incidents/incidentPatternSemantics';
import {
  getIncidentPatternReadVolumeCount,
  getIncidentPatternWindowSignalCount,
  resolvePatternVolumeKind,
  type PatternVolumeKind,
} from '../../domain/incidents/patternReviewScopeSemantics';
import {
  readFollowupOwnerLabel,
  readPatternOwnerLabel,
} from '../../domain/incidents/incidentOwnershipSemantics';
import {
  formatRelatedOccurrenceCount,
  normalizeGroupingBasis,
  patternActionScopeHint,
  patternDecisionActionScopePendingLine,
  PATTERN_ACTION_SCOPE_BASELINE,
  patternQueueVolumeSummary,
  patternRollupExplanation,
  patternVolumeActionScopeWarningCopy,
  buildGroupedWhatToKnowFirst,
  representativeIncidentAuditHint,
} from '../../domain/incidents/incidentPatternVocabulary';
import { resolveGroupedReviewWindowDisplayCount } from '../../domain/incidents/groupedReviewWindowCopy';
import type { AfterSignedScanReviewContext } from '../../domain/incidents/afterSignedScanReview';
import type { IncidentTimeRange } from '../../pages/incidents/useIncidentsTimeRange';
import {
  formatObservationBadge,
  formatObservationCountLine,
  resolveReviewWindowContext,
  type ReviewWindowContext,
} from '../../domain/incidents/reviewWindowContext';
import {
  displayPatternEvidenceProofLabel,
  displayPatternWorkflowLabel,
} from '../../domain/incidents/patternReviewDisplayLabels';

type BuildGroupedIncidentReviewModelArgs = {
  selectedIncident: Incident | null | undefined;
  selectedGroupContext: IncidentGroup | null | undefined;
  reviewExactMode?: boolean;
  selectedZoneName: string | null;
  selectedGroupZoneName: string | null;
  selectedCameraName: string | null;
  selectedGroupCameraName: string | null;
  canManageGroupPatterns: boolean;
  workflowLabel: string;
  opsStateLabel: string;
  proofSummary: string;
  summaryExplanation: string;
  attentionReasonSummary: string;
  reviewFocusSummary: string;
  riskPatternSummary: string;
  riskPatternLabel?: string;
  detectionRuleSummary: string;
  nextReviewSummary: string;
  patternBucketCount?: number;
  safeText: (value: unknown) => string;
  presentAreaLabel: (value: string) => string;
  formatTimingInstant: (value: number | null | undefined) => string;
  parseTimingMs: (value: unknown) => number | null;
  titleCaseWords: (value: string) => string;
  reviewWindowContext?: ReviewWindowContext;
  timeRange?: IncidentTimeRange;
  afterSignedScanReview?: AfterSignedScanReviewContext | null;
  fullHistoryMode?: boolean;
};

export type PatternSummaryView = {
  patternTitle: string;
  /** Read-side episode / lookback volume (banner). */
  patternVolumeCount: number;
  /** Signals in selected review window (queue rollup). */
  windowSignalCount: number;
  /** @deprecated use patternVolumeCount — kept for callers expecting relatedOccurrenceCount */
  relatedOccurrenceCount: number;
  patternVolumeKind: PatternVolumeKind;
  /** Count for grouped review badges (selected window, not lookback). */
  reviewWindowDisplayCount: number;
  patternVolumeDisplay: string;
  /** Compact badge line for status row chips. */
  patternVolumeBadge: string;
  patternBucketCount: number;
  recurrenceLabel: string;
  firstSeen: string;
  lastSeen: string;
  zoneId: string;
  cameraId: string;
  deviceId: string;
  policyId: string;
  policyName: string;
  groupingBasis: string;
  readScopeDescription: string;
  displaySummary: string;
  rollupText: string;
  queueVolumeText: string;
};

export type RepresentativeIncidentSummaryView = {
  incidentId: string;
  evidenceStatus: string;
  workflowStatus: string;
  reviewStatus: string;
  currentAssignee: string;
  currentPatternOwner: string;
  currentFollowupOwner: string;
  isRepresentativeIncident: boolean;
  auditHint: string;
};

export type ActionScopeSummaryView = {
  actionScopeKnown: boolean;
  patternVolumeCount: number;
  windowSignalCount: number;
  readSideOccurrenceCount: number;
  backendMatchedCount: number | null;
  volumeDiffersFromActionScope: boolean;
  scopeWarning: ReturnType<typeof patternVolumeActionScopeWarningCopy> | null;
  baselineLine: string;
  patternVolumeLine: string;
  actionScopeLine: string;
  readScopeDescription: string;
  mutationScopeDescription: string;
  ambiguousScopeWarning: string;
  refreshRequiredMessage: string;
  /** Flat copy for Pattern Decision card */
  displayLine: string;
};

export type GroupedIncidentReviewModel = {
  groupedReviewMode: boolean;
  selectedGroupKey: string;
  pattern: PatternSummaryView;
  representative: RepresentativeIncidentSummaryView;
  actionScope: ActionScopeSummaryView;
  /** @deprecated prefer pattern.displaySummary */
  patternSummary: string;
  /** @deprecated prefer pattern.rollupText */
  patternRollupText: string;
  /** @deprecated prefer pattern.groupingBasis */
  groupingBasisText: string;
  /** @deprecated prefer pattern.queueVolumeText */
  patternQueueVolumeText: string;
  relatedOccurrenceCount: number;
  observedOccurrenceLabel: string;
  representativeIncidentId: string;
  /** @deprecated prefer representative.auditHint */
  representativeIncidentSummary: string;
  /** @deprecated prefer actionScope.displayLine */
  actionScopeSummary: string;
  groupedScopeSummary: string;
  groupedPolicyLabel: string;
  followUpOwnerSummary: string;
  workOrderSummary: string;
  patternActionAvailabilityMessage: string;
  groupedFirstSeenLabel: string;
  groupedLastSeenLabel: string;
  groupedEvidenceStatusLabel: string;
  groupedWhatToKnow: string;
  groupedNextReviewSummary: string;
  followUpStateSummary: string;
  reviewWindowContext: ReviewWindowContext;
};

const resolveBuyerZoneLabel = (
  value: string | null | undefined,
  presentAreaLabel: (value: string) => string,
  safeText: (value: unknown) => string,
): string => {
  const raw = safeText(value);
  if (!raw) return '';
  if (/demo5tes/i.test(raw)) return 'Restricted Forklift Zone';
  return presentAreaLabel(raw);
};

export function buildGroupedIncidentReviewModel({
  selectedIncident,
  selectedGroupContext,
  reviewExactMode = false,
  selectedZoneName,
  selectedGroupZoneName,
  selectedCameraName,
  selectedGroupCameraName,
  canManageGroupPatterns,
  workflowLabel,
  opsStateLabel,
  proofSummary,
  summaryExplanation,
  attentionReasonSummary,
  reviewFocusSummary,
  riskPatternSummary,
  riskPatternLabel = '',
  detectionRuleSummary,
  nextReviewSummary,
  patternBucketCount = 0,
  safeText,
  presentAreaLabel,
  formatTimingInstant,
  parseTimingMs,
  titleCaseWords: _titleCaseWords,
  reviewWindowContext: reviewWindowContextInput,
  timeRange,
  afterSignedScanReview,
  fullHistoryMode = false,
}: BuildGroupedIncidentReviewModelArgs): GroupedIncidentReviewModel {
  void _titleCaseWords;
  const reviewWindowContext = reviewWindowContextInput ?? resolveReviewWindowContext({
    timeRange: timeRange ?? { label: 'Last 24h' },
    afterSignedScanReview,
    fullHistoryMode,
  });
  const groupedReviewMode = isGroupedIncident(selectedIncident as any, selectedGroupContext || null);
  const buyerZoneLabel = resolveBuyerZoneLabel(
    selectedGroupZoneName || selectedZoneName || safeText(selectedIncident?.zone_id || (selectedGroupContext as any)?.zone_id),
    presentAreaLabel,
    safeText,
  );
  const zoneLabel = buyerZoneLabel ? `in ${buyerZoneLabel}` : '';
  const selectedGroupKey = safeText((selectedGroupContext as any)?.group_key);
  const patternVolumeKind = resolvePatternVolumeKind(selectedGroupContext as any);
  const patternVolumeCount = Math.max(
    getIncidentPatternReadVolumeCount(selectedGroupContext as any),
    getIncidentPatternReadVolumeCount(selectedIncident as any),
  );
  const windowSignalCount = Math.max(
    getIncidentPatternWindowSignalCount(selectedGroupContext as any),
    getIncidentPatternWindowSignalCount(selectedIncident as any),
  );
  const reviewWindowDisplayCount = (() => {
    if (reviewWindowContext.kind === 'full_case_history') {
      return patternVolumeCount;
    }
    if (groupedReviewMode) {
      return resolveGroupedReviewWindowDisplayCount(windowSignalCount, patternVolumeCount);
    }
    return patternVolumeCount;
  })();
  const relatedOccurrenceCount = patternVolumeCount;
  const groupingBasisText = normalizeGroupingBasis(String((selectedGroupContext as any)?.group_reason || ''));
  const patternRollupText = selectedGroupKey || reviewWindowDisplayCount > 1
    ? patternRollupExplanation(
      reviewWindowDisplayCount,
      zoneLabel ? ` ${zoneLabel}` : '',
      'review_window',
    )
    : '';
  const recurrenceSuffix = safeText(riskPatternLabel)
    ? ` The queue also reports a broader recurrence signal (${safeText(riskPatternLabel)}).`
    : '';
  const patternQueueVolumeText = (() => {
    const base = patternQueueVolumeSummary(relatedOccurrenceCount, groupingBasisText);
    return base ? `${base}${recurrenceSuffix}` : recurrenceSuffix.trim();
  })();
  const representativeIncidentId = safeText(
    (reviewExactMode ? selectedIncident?.incident_id : '')
    || (selectedGroupContext as any)?.pattern_source?.representative_incident_id
    || (selectedGroupContext as any)?.linked_detailed_record_id
    || selectedIncident?.incident_id,
  );
  const reviewAnchorIncidentId = safeText((selectedGroupContext as any)?.review_anchor_incident_id || representativeIncidentId);
  const reviewAnchorSelectionReason = safeText((selectedGroupContext as any)?.review_anchor_selection_reason);
  const reviewReadiness = safeText((selectedGroupContext as any)?.review_readiness).toLowerCase();
  const latestIncidentId = safeText(
    (selectedGroupContext as any)?.latest_incident_id || (selectedGroupContext as any)?.linked_detailed_record_id,
  );
  const latestIncidentEvidenceState = safeText((selectedGroupContext as any)?.latest_incident_evidence_state);
  const anchorReuseAllowed = Boolean((selectedGroupContext as any)?.anchor_reuse_allowed);
  const latestIncidentEvidenceLabel = (() => {
    switch (latestIncidentEvidenceState) {
      case 'evidence_complete':
        return 'snapshot and clip ready';
      case 'snapshot_ready_clip_processing':
        return 'snapshot ready, clip processing';
      case 'clip_ready_snapshot_processing':
        return 'clip ready, snapshot processing';
      case 'processing':
        return 'proof still processing';
      default:
        return 'latest proof state unknown';
    }
  })();
  const representativeAuditSummary = (() => {
    const parts = [representativeIncidentAuditHint(reviewAnchorIncidentId)];
    if (reviewAnchorSelectionReason === 'newest_eligible_evidence_complete') {
      parts.push(`Review anchor ${reviewAnchorIncidentId} was chosen as the newest eligible evidence sample in this pattern with snapshot and clip ready.`);
    } else if (reviewAnchorSelectionReason === 'best_available_proof_in_pattern') {
      parts.push(`Review anchor ${reviewAnchorIncidentId} was chosen as the best available proof in this pattern because no newer evidence-complete sample was eligible.`);
    } else if (reviewAnchorSelectionReason === 'fallback_latest_no_complete_evidence') {
      parts.push('No eligible evidence-complete anchor is available yet, so the latest observation is shown only as placeholder context for this pattern.');
    }
    if (latestIncidentId && latestIncidentId !== reviewAnchorIncidentId) {
      parts.push(`Latest observation is ${latestIncidentId} (${latestIncidentEvidenceLabel}); it is not the review subject yet.`);
    }
    if (reviewReadiness === 'evidence_preparing') {
      parts.push('Formal evidence review is not ready yet because proof is still being prepared.');
    } else if (reviewReadiness === 'no_eligible_anchor') {
      parts.push('Formal evidence review is blocked because no eligible review anchor is available yet.');
    } else if (reviewReadiness === 'ready_reused') {
      parts.push('This review is ready on a reused anchor because policy allows replay of a previously completed representative review.');
    }
    if (!anchorReuseAllowed) {
      parts.push('Completed representative reviews are not reused by default.');
    }
    return parts.join(' ');
  })();
  const groupedScopeSummary = [
    buyerZoneLabel,
    selectedCameraName || selectedGroupCameraName,
    safeText(selectedIncident?.device_id || (selectedGroupContext as any)?.device_id),
  ].filter(Boolean).join(' · ');
  const groupedPolicyLabel = safeText(
    detectionRuleSummary
    || (selectedGroupContext as any)?.policy_name
    || (selectedGroupContext as any)?.policy_id,
  );
  const currentPatternOwner = (
    readPatternOwnerLabel(selectedIncident as any)
    || safeText((selectedGroupContext as any)?.canonical_pattern_owner)
    || safeText((selectedGroupContext as any)?.pattern_owner_display)
    || safeText((selectedGroupContext as any)?.pattern_owner)
    || 'Unassigned'
  );
  const currentFollowupOwner = readFollowupOwnerLabel(selectedIncident as any) || 'Unassigned';
  const followUpOwnerSummary = currentFollowupOwner === 'Unassigned' ? 'Unassigned' : currentFollowupOwner;
  const workOrderSummary = (() => {
    const ticketState = safeText((selectedIncident as any)?.odoo_ticket_state);
    const ticketId = safeText((selectedIncident as any)?.odoo_ticket_id);
    if (ticketId && ticketState) return `Work order linked (${ticketState})`;
    if (ticketId) return `Work order linked (${ticketId})`;
    return 'No work order linked yet';
  })();
  const resolvedGroupActionTarget = selectedGroupContext
    ? resolveIncidentGroupActionTarget(selectedGroupContext as IncidentGroup)
    : null;
  const actionScopeKnown = Boolean(resolvedGroupActionTarget?.groupKey);
  const patternActionAvailabilityMessage = !actionScopeKnown
    ? 'Action scope is ambiguous. Recurring-risk action is disabled until backend membership is resolved.'
    : !canManageGroupPatterns
      ? 'Recurring-risk actions are unavailable for this review context or your current permissions.'
      : '';
  const groupedFirstSeenLabel = formatTimingInstant(
    parseTimingMs(
      (selectedGroupContext as any)?.first_seen
      || (selectedGroupContext as any)?.started_at
      || (selectedIncident as any)?.raw?.first_seen
      || selectedIncident?.created_at,
    ),
  );
  const groupedLastSeenLabel = formatTimingInstant(
    parseTimingMs(
      (selectedGroupContext as any)?.last_seen
      || (selectedGroupContext as any)?.last_seen_at
      || (selectedIncident as any)?.raw?.last_seen
      || selectedIncident?.updated_at
      || selectedIncident?.created_at,
    ),
  );
  const groupedEvidenceStatusLabel = displayPatternEvidenceProofLabel(
    proofSummary || workflowLabel || 'Not evaluated',
  );
  const patternTitle = safeText(
    (selectedGroupContext as any)?.summary_text
    || (selectedIncident as any)?.title
    || riskPatternSummary
    || summaryExplanation,
  );
  const displaySummary = patternRollupText || riskPatternSummary || summaryExplanation;
  const patternVolumeDisplay = formatObservationCountLine(reviewWindowDisplayCount, reviewWindowContext);
  const patternVolumeBadge = formatObservationBadge(reviewWindowDisplayCount, reviewWindowContext);
  const mutationScopeDescription = patternActionScopeHint(null);
  const actionScope: ActionScopeSummaryView = {
    actionScopeKnown,
    patternVolumeCount,
    windowSignalCount,
    readSideOccurrenceCount: patternVolumeCount,
    backendMatchedCount: null,
    volumeDiffersFromActionScope: false,
    scopeWarning: null,
    baselineLine: PATTERN_ACTION_SCOPE_BASELINE,
    patternVolumeLine: patternVolumeBadge,
    actionScopeLine: patternDecisionActionScopePendingLine(),
    readScopeDescription: patternVolumeDisplay,
    mutationScopeDescription,
    ambiguousScopeWarning: patternActionAvailabilityMessage,
    refreshRequiredMessage:
      'Refresh count in Close / Assign / Suppress to resolve the affected open linked records.',
    displayLine: mutationScopeDescription,
  };
  const pattern: PatternSummaryView = {
    patternTitle,
    patternVolumeCount,
    windowSignalCount,
    reviewWindowDisplayCount,
    relatedOccurrenceCount,
    patternVolumeKind,
    patternVolumeDisplay,
    patternVolumeBadge,
    patternBucketCount: Math.max(0, Number(patternBucketCount || 0)),
    recurrenceLabel: safeText(riskPatternLabel),
    firstSeen: groupedFirstSeenLabel,
    lastSeen: groupedLastSeenLabel,
    zoneId: safeText(selectedIncident?.zone_id || (selectedGroupContext as any)?.zone_id),
    cameraId: safeText(selectedIncident?.camera_id || (selectedGroupContext as any)?.camera_id),
    deviceId: safeText(selectedIncident?.device_id || (selectedGroupContext as any)?.device_id),
    policyId: safeText((selectedGroupContext as any)?.policy_id || (selectedIncident as any)?.policy_id),
    policyName: groupedPolicyLabel,
    groupingBasis: groupingBasisText,
    readScopeDescription: patternVolumeDisplay,
    displaySummary,
    rollupText: patternRollupText,
    queueVolumeText: patternQueueVolumeText,
  };
  const representative: RepresentativeIncidentSummaryView = {
    incidentId: reviewAnchorIncidentId || representativeIncidentId,
    evidenceStatus: groupedEvidenceStatusLabel,
    workflowStatus: displayPatternWorkflowLabel(workflowLabel) || 'Needs review',
    reviewStatus: opsStateLabel || nextReviewSummary || 'Needs review',
    currentAssignee: safeText((selectedIncident as any)?.assignee),
    currentPatternOwner,
    currentFollowupOwner,
    isRepresentativeIncident: Boolean(reviewAnchorIncidentId || representativeIncidentId),
    auditHint: representativeAuditSummary,
  };

  return {
    groupedReviewMode,
    selectedGroupKey,
    pattern,
    representative,
    actionScope,
    patternSummary: displaySummary,
    patternRollupText,
    groupingBasisText,
    patternQueueVolumeText,
    relatedOccurrenceCount,
    observedOccurrenceLabel: formatRelatedOccurrenceCount(relatedOccurrenceCount),
    representativeIncidentId: representative.incidentId,
    representativeIncidentSummary: representative.auditHint,
    actionScopeSummary: actionScope.displayLine,
    groupedScopeSummary,
    groupedPolicyLabel,
    followUpOwnerSummary,
    workOrderSummary,
    patternActionAvailabilityMessage,
    groupedFirstSeenLabel,
    groupedLastSeenLabel,
    groupedEvidenceStatusLabel,
    groupedWhatToKnow: buildGroupedWhatToKnowFirst({
      patternOwnerLabel: currentPatternOwner,
      attentionReasonSummary,
      reviewFocusSummary,
    }),
    followUpStateSummary: opsStateLabel || nextReviewSummary || 'Needs review',
    groupedNextReviewSummary: (() => {
      const raw = safeText(nextReviewSummary);
      if (/assign one below/i.test(raw)) {
        return 'No next review time yet. Set it in Corrective action when you schedule follow-up.';
      }
      return raw || 'No next review time set';
    })(),
    reviewWindowContext,
  };
}
